import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Navigate } from "react-router-dom";
import { atom, useRecoilState, useSetRecoilState } from "recoil";
import { useApi } from "../hooks/useApi";
import { recoilPersist } from "recoil-persist";
import LoginInput from "../components/Input/LoginInput";
import PwResettingModal from "../components/pages/Login/PwResettingModal";
import LoginErrorMessage from "../components/Login/LoginErrorMessage";
import FindPwModal from "../components/pages/Login/FindPwModal";
import AlertModal from "../components/Modal/AlertModal";
import CryptoJS from "crypto-js";
import Loader from "../components/DefaultStyles/Loader";
const { persistAtom } = recoilPersist();

export const userDataType = atom({
  key: "userDataType",
  default: {
    name: "",
    email: "",
    grade: "",
    sid: "",
    collabo: 0,
  },
  effects_UNSTABLE: [persistAtom],
});

export const errCountType = atom({
  key: "errCountType",
  default: 0,
  effects_UNSTABLE: [persistAtom],
});

export const loginErrMsgString = (
  code: number | undefined | string,
  count: number | undefined
) => {
  switch (code) {
    case 400:
      return `비밀번호가 일치하지 않습니다. (${count}/5)`;

    case 401:
      return `등록되지 않은 계정입니다.`;

    case 403:
      return `메일로 받은 임시 비밀번호로 로그인 해주세요.`;

    case 410:
      return `비밀번호 오류 횟수가 초과되었습니다.`;

    default:
      return "";
  }
};

export const authType = atom({
  key: "authType",
  default: {},
  effects_UNSTABLE: [persistAtom],
});

export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [otp, setOtp] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | undefined>("");
  const [errCode, setErrCode] = useState<number | undefined | string>(0);
  const [pwResettingModal, setPwResettingModal] = useState(false);
  const [findPwModal, setFindPwModal] = useState(false);
  const [exceedInputCountAlert, setExceedInputCountAlert] = useState(false);
  const [executeLogin, setExecuteLogin] = useState(false);
  const [user, setUser] = useState(null);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [errCnt, setErrCnt] = useRecoilState(errCountType);
  const [needAuthPin, setNeedAuthPin] = useState(false);

  const setUserData = useSetRecoilState(userDataType);

  const {
    response: loginResponse,
    error: loginError,
    loading: loginLoading,
  } = useApi({
    url: executeLogin ? "login" : undefined,
    method: "post",
    data: {
      email,
      password: CryptoJS.enc.Hex.stringify(CryptoJS.SHA1(pw)),
      isSunset: 0,
      authpin: otp !== "" ? otp : null,
    },
  });

  console.log(loginResponse);
  console.log(loginError);
  console.log(errCnt);
  console.log(errCode);

  useEffect(() => {
    if (loginResponse?.data?.code === 200) {
      setUser(loginResponse?.data);
      setUserData(loginResponse?.data);
      sessionStorage.setItem("sid", loginResponse?.data?.sid);
      sessionStorage.setItem("email", loginResponse?.data?.email);
      sessionStorage.setItem("grade", loginResponse?.data?.grade);
      sessionStorage.setItem("name", loginResponse?.data?.name);
      sessionStorage.setItem("collabo", loginResponse?.data?.collabo);
    } else if (loginResponse?.data?.code === 411) {
      setExecuteLogin(false);
      setNeedAuthPin(true);
      setBtnDisabled(false);
      setErrorMsg(
        "OTP 인증 코드를 메일로 발송하였습니다. 메일함을 확인해 주세요."
      );
    } else if (loginResponse?.data?.code === 412) {
      setNeedAuthPin(true);
      setExecuteLogin(false);
      setBtnDisabled(false);
      setErrorMsg("OTP 인증 코드가 틀렸습니다. 다시 입력해 주세요.");
    } else {
      setErrCode(loginResponse?.data?.code);
      setErrCnt(loginResponse?.data?.count);

      setErrorMsg(
        `${
          loginResponse?.data
            ? loginErrMsgString(
                loginResponse?.data?.code,
                loginResponse?.data?.count
              )
            : ""
        }`
      );
      setExecuteLogin(false);
      setBtnDisabled(false);

      if (loginResponse?.data?.code === 403) {
        setExceedInputCountAlert(true);
      } else if (loginResponse?.data?.code === 410) {
        setPwResettingModal(true);
      }
    }
  }, [loginResponse, setUserData]);

  console.log(email === "" || pw === "" || btnDisabled);

  return (
    <LoginContainer>
      <LoginForm>
        <LoginTitle>
          <LinkidLogo
            src={require("../assets/login/logo-linkidwordmark@2x.png")}
          />
          Admin
        </LoginTitle>

        <LoginInputsContainer>
          {/* 이메일 주소 */}
          <LoginInput
            label="이메일 주소"
            value={email}
            onChangeEvent={(e) => {
              setEmail(e.target.value);

              if (errorMsg !== "") {
                setBtnDisabled(false);
              }
            }}
            hasError={errCode === 401}
          />

          {/* 비밀번호 */}
          <LoginInput
            label="비밀번호"
            value={pw}
            onChangeEvent={(e) => {
              setPw(e.target.value);

              if (errorMsg !== "") {
                setBtnDisabled(false);
              }
            }}
            isPassword={true}
            hasError={errCode === 400}
            onKeyUpEvent={(e) => {
              if (e.key === "Enter") {
                setExecuteLogin(true);
                setBtnDisabled(true);
              }
            }}
          />

          {!needAuthPin && errorMsg !== "" && (
            <LoginErrorMessage msg={errorMsg!} />
          )}

          {needAuthPin && (
            <LoginInput
              label="OTP 입력"
              value={otp}
              onChangeEvent={(e) => {
                setOtp(e.target.value);
              }}
              isPassword={true}
              hasError={errCode === 412}
              textType="number"
              onKeyUpEvent={(e) => {
                if (e.key === "Enter") {
                  setExecuteLogin(true);
                  setBtnDisabled(true);
                }
              }}
              inputMode="numeric"
            />
          )}

          {needAuthPin && errorMsg !== "" && (
            <LoginErrorMessage msg={errorMsg!} />
          )}
        </LoginInputsContainer>

        <LoginBtn
          disabled={
            email === "" ||
            pw === "" ||
            btnDisabled ||
            (needAuthPin ? otp.length !== 6 : false)
          }
          onClick={() => {
            setExecuteLogin(true);
            setBtnDisabled(true);
          }}
        >
          로그인
        </LoginBtn>

        <PasswordResettingContainer>
          <PasswordResettingBtn onClick={() => setFindPwModal(true)}>
            비밀번호 찾기
          </PasswordResettingBtn>
        </PasswordResettingContainer>
      </LoginForm>

      <>{user && <Navigate to="/"></Navigate>}</>

      {/* 비밀번호 재설정 모달 */}
      {pwResettingModal && (
        <PwResettingModal
          closeEvent={() => setPwResettingModal(false)}
          email={email}
          originPw={pw}
        />
      )}

      {/* 비밀번호 찾기 모달 - 이메일 확인 */}
      {findPwModal && <FindPwModal closeEvent={() => setFindPwModal(false)} />}

      {/* 비밀번호 입력 횟수 초과, 임시 비밀번호 안내 모달  */}
      {exceedInputCountAlert && (
        <AlertModal
          btnColor="mint"
          title={
            "비밀번호 입력 횟수가 초과되어 이메일로 임시 비밀번호를 발급해 드렸어요.\n임시 비밀번호로 다시 로그인해 주세요."
          }
          customCancelBtn={<></>}
          confirmEvent={() => setExceedInputCountAlert(false)}
        />
      )}

      {loginLoading && <Loader />}
    </LoginContainer>
  );
}

const LoginContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.color.bgColor};

  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginForm = styled.div`
  max-width: 420px;
  width: 100%;
  padding: 0 20px;
`;

const LoginTitle = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  font-size: 26px;
  letter-spacing: 0.5px;
  text-align: left;
  color: #858c97;
`;

const LinkidLogo = styled.img`
  width: 98px;
  margin-right: 4px;
`;

const LoginInputsContainer = styled.div`
  margin: 36px 0 24px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LoginBtn = styled.button`
  width: 100%;
  height: 55px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.buttonColor.blueNormal};

  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  text-align: center;
  color: #ffffff;

  &:disabled {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.buttonColor.blueDisabled};

    &:hover {
      background-color: ${({ theme }) => theme.buttonColor.blueDisabled};
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.buttonColor.blueHover};
  }
`;

const PasswordResettingContainer = styled.div`
  margin-top: 40px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const PasswordResettingBtn = styled.button`
  font-size: 15px;
  font-weight: 500;
  line-height: 22.5px;
  text-align: center;
  color: #858c97;
  text-decoration: underline;
`;
